import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, X, Mail, Copy, ShieldCheck, GitMerge, Grid3x3, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import MergeUsersDialog from "@/components/MergeUsersDialog";

const BASE_ROLES = ["admin", "client_manager", "designer", "engineer", "strategist", "operations", "intern"];
const BUS = ["Strategy", "BU1", "BU2", "BU3", "Operations", "Founder"];

const emptyUserForm = () => ({
  email: "", name: "",
  role: "designer", business_unit: "BU1", title: "", project_ids: [],
});

export default function AdminUsers() {
  const { user: current } = useAuth();
  const navigate = useNavigate();
  const isFounder = current?.role === "founder";
  // Phase 84.45 — "founder" is only offered as a selectable role when the
  // person editing is themselves a Founder (the backend enforces this too —
  // this is just so a non-founder editor never sees an option that would 403).
  const ROLES = isFounder ? ["founder", ...BASE_ROLES] : BASE_ROLES;
  const [mergeOpen, setMergeOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUserForm);
  const [selected, setSelected] = useState(new Set());
  // Phase 117 — split the roster into "Studio Team" (everyone except
  // role=client) and "Client Accounts" (role=client only) so the two
  // audiences never get mixed in one list.
  const [rosterTab, setRosterTab] = useState("studio");
  // Phase 113 — a client row can be edited (name/title/visible projects)
  // but its role is never reassignable through this generic staff dialog.
  const EDIT_ROLES = editing?.role === "client" ? [...new Set(["client", ...ROLES])] : ROLES;

  const studioUsers = useMemo(() => {
    const list = users.filter((u) => u.role !== "client");
    // Phase 137 — Team Registry auto-sort: Active first, then On Notice
    // (still "active" but a last_working_day is on file), Inactive /
    // Resigned always at the bottom. Display-order only — never touches
    // Access Management's own row ordering.
    const rank = (u) => (u.status === "inactive" ? 2 : u.last_working_day ? 1 : 0);
    return [...list].sort((a, b) => rank(a) - rank(b));
  }, [users]);
  const clientUsers = useMemo(() => users.filter((u) => u.role === "client"), [users]);
  const visibleUsers = rosterTab === "clients" ? clientUsers : studioUsers;

  const toggle = (uid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });
  const toggleAll = () => {
    setSelected((prev) => (prev.size === visibleUsers.length ? new Set() : new Set(visibleUsers.map((u) => u.id))));
  };
  const clearSelection = () => setSelected(new Set());
  const switchRosterTab = (tab) => { setRosterTab(tab); clearSelection(); };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} ${rosterTab === "clients" ? "client account" : "team member"}${selected.size === 1 ? "" : "s"}? They'll be removed from all projects and channels.`)) return;
    try {
      const ids = Array.from(selected);
      const results = await Promise.allSettled(ids.map((id) => api.delete(`/admin/users/${id}`)));
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed) toast.error(`${failed} of ${ids.length} could not be removed`);
      else toast.success(`Removed ${ids.length} member${ids.length === 1 ? "" : "s"}`);
      clearSelection();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = useCallback(async () => {
    const [u, p] = await Promise.all([api.get("/users"), api.get("/projects")]);
    setUsers(u.data.filter((x) => x.role !== "vendor"));
    setProjects(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Open onboarding dialog when URL has ?new=1 (from global "+ Create")
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("new") === "1") {
      setEditing(null); setForm(emptyUserForm()); setOpen(true);
      params.delete("new"); setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyUserForm());
    setOpen(true);
  };

  const [inviteResult, setInviteResult] = useState(null);

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, name: u.name, role: u.role,
      business_unit: u.business_unit || "BU1", title: u.title || "",
      project_ids: u.project_ids || [],
    });
    setOpen(true);
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete team member "${u.name}"? They will be removed from all projects and channels.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Member removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 90 — Admin/Founder-triggered instant password reset (no email).
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetPwSaving, setResetPwSaving] = useState(false);
  const submitResetPassword = async () => {
    if (resetPw.length < 8) { toast.error("At least 8 characters please."); return; }
    setResetPwSaving(true);
    try {
      await api.post(`/users/${resetPwUser.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetPwUser.name}`);
      setResetPwUser(null);
      setResetPw("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResetPwSaving(false); }
  };

  const resendInvite = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/resend-invitation`);
      setInviteResult({ email: u.email, name: u.name, ...data });
      toast.success(`Invitation re-sent to ${u.email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    try {
      if (editing) {
        const body = { name: form.name, role: form.role, business_unit: form.business_unit, title: form.title, project_ids: form.project_ids };
        await api.patch(`/admin/users/${editing.id}`, body);
        toast.success("Updated");
        setOpen(false);
      } else {
        const { data } = await api.post("/admin/users", form);
        if (data.invitation) {
          setInviteResult({ email: data.email, name: data.name, ...data.invitation });
          toast.success(`Invitation emailed to ${data.email}`);
          setOpen(false);
        } else {
          toast.success("User onboarded");
          setOpen(false);
        }
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleProject = (pid) => {
    setForm((f) => f.project_ids.includes(pid)
      ? { ...f, project_ids: f.project_ids.filter((x) => x !== pid) }
      : { ...f, project_ids: [...f.project_ids, pid] });
  };

  const projectsByBU = useMemo(() => {
    const m = {};
    for (const p of projects) {
      (m[p.business_unit] = m[p.business_unit] || []).push(p);
    }
    return m;
  }, [projects]);

  const projectsMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="admin-users-page">
      <PageHeader
        eyebrow="People"
        title="Team"
        subtitle={rosterTab === "clients"
          ? "Manage client portal access — reset passwords, review project visibility."
          : (isFounder ? "Onboard new team members and assign them to a Business Unit + projects. Page/action access lives in Access Management." : "Onboard new team members and assign them to a Business Unit + projects.")}
        actions={
          <div className="flex items-center gap-2">
            {isFounder && rosterTab === "studio" && (
              <Button
                onClick={() => navigate("/access-management")}
                variant="outline"
                className="rounded-sm"
                data-testid="access-matrix-btn"
              >
                <Grid3x3 size={14} className="mr-1.5" />Access Management
              </Button>
            )}
            {isFounder && rosterTab === "studio" && (
              <Button
                onClick={() => setMergeOpen(true)}
                variant="outline"
                className="rounded-sm"
                data-testid="merge-users-btn"
              >
                <GitMerge size={14} className="mr-1.5" />Merge duplicates
              </Button>
            )}
            {rosterTab === "clients" ? (
              <Button onClick={() => navigate("/crm?tab=clients&new=1")} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="new-client-cta-btn"><Plus size={14} className="mr-1.5" />Onboard client</Button>
            ) : (
              <Button onClick={startCreate} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="new-user-btn"><Plus size={14} className="mr-1.5" />Add team member</Button>
            )}
          </div>
        }
      />

      {/* Phase 117 — Studio Team vs Client Accounts. Both feed off the same
          `/users` list, split purely by role, so onboarding through either
          wizard (this page's "Add team member", or the CRM's "Onboard
          client") permanently lands the new user in the matching tab. */}
      <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden bg-white mb-5" data-testid="roster-tabs">
        <button
          type="button"
          onClick={() => switchRosterTab("studio")}
          className={`px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2 ${rosterTab === "studio" ? "bg-[#F54800] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="roster-tab-studio"
        >
          Studio Team
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${rosterTab === "studio" ? "bg-white/20" : "bg-slate-100"}`}>{studioUsers.length}</span>
        </button>
        <button
          type="button"
          onClick={() => switchRosterTab("clients")}
          className={`px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2 border-l border-slate-200 ${rosterTab === "clients" ? "bg-[#F54800] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="roster-tab-clients"
        >
          Client Accounts
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${rosterTab === "clients" ? "bg-white/20" : "bg-slate-100"}`}>{clientUsers.length}</span>
        </button>
      </div>

      {selected.size > 0 && (
        <div className="card-flat px-5 py-3 mb-3 flex items-center justify-between bg-orange-50 border-orange-200" data-testid="bulk-action-bar">
          <div className="text-sm">
            <span className="font-semibold text-[#F54800]">{selected.size}</span> selected
            <button onClick={clearSelection} className="ml-3 text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="bulk-clear"><X size={12} /> Clear</button>
          </div>
          <Button onClick={bulkDelete} size="sm" className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-delete">
            <Trash2 size={14} className="mr-1.5" /> Delete selected
          </Button>
        </div>
      )}

      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-200 label-overline items-center">
              <div className="col-span-1">
                <Checkbox
                  checked={visibleUsers.length > 0 && selected.size === visibleUsers.length}
                  onCheckedChange={toggleAll}
                  data-testid="bulk-select-all"
                  aria-label="Select all"
                />
              </div>
              {rosterTab === "clients" ? (
                <>
                  <div className="col-span-3">Member</div><div className="col-span-3">Company</div><div className="col-span-3">Projects</div><div className="col-span-2 text-right">Actions</div>
                </>
              ) : (
                <>
                  <div className="col-span-3">Member</div><div className="col-span-2">Role</div><div className="col-span-1">BU</div><div className="col-span-3">Projects</div><div className="col-span-2 text-right">Actions</div>
                </>
              )}
            </div>
            <div className="divide-row">
              {visibleUsers.map((u) => {
                const isSel = selected.has(u.id);
                return (
                  <div key={u.id} className={`grid grid-cols-12 gap-4 px-5 py-3 text-sm items-center hover-row ${isSel ? "bg-orange-50" : ""}`} data-testid={`user-row-${u.id}`}>
                    <div className="col-span-1">
                      <Checkbox
                        checked={isSel}
                        onCheckedChange={() => toggle(u.id)}
                        data-testid={`select-user-${u.id}`}
                        aria-label={`Select ${u.name}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <div className="text-slate-900 font-medium flex items-center gap-1.5">
                        {u.name}
                        {u.invitation_pending && <Tag tone="orange" data-testid={`invite-pending-${u.id}`}>invite sent</Tag>}
                        {u.status === "inactive" && <Tag tone="slate" data-testid={`inactive-badge-${u.id}`}>Inactive</Tag>}
                        {u.status !== "inactive" && u.last_working_day && <Tag tone="yellow" data-testid={`on-notice-badge-${u.id}`}>On notice</Tag>}
                      </div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </div>
                    {rosterTab === "clients" ? (
                      <div className="col-span-3 text-slate-600">{u.company || "—"}</div>
                    ) : (
                      <>
                        <div className="col-span-2 capitalize">{u.role.replace("_", " ")}</div>
                        <div className="col-span-1">{u.business_unit || "—"}</div>
                      </>
                    )}
                    <div className="col-span-3 text-xs text-slate-600 truncate">
                      {(u.project_ids || []).length ? (u.project_ids || []).map((pid) => projectsMap[pid]?.name || pid).join(", ") : "—"}
                    </div>
                    <div className="col-span-2 text-right inline-flex items-center justify-end gap-1">
                      {u.invitation_pending && (
                        <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => resendInvite(u)} title="Re-send invitation" data-testid={`resend-invite-${u.id}`}><Mail size={12} /></Button>
                      )}
                      {rosterTab === "clients" ? (
                        <>
                          {/* Phase 117 — Reset password is the primary action for
                              client rows (portal-access management is the main
                              reason a Founder/Admin visits this tab). */}
                          <Button size="sm" className="rounded-sm h-7 bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={() => { setResetPwUser(u); setResetPw(""); }} title="Reset password" data-testid={`reset-password-user-${u.id}`}><KeyRound size={12} className="mr-1" />Reset password</Button>
                          <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil size={12} /></Button>
                          <Button size="sm" variant="outline" className="rounded-sm h-7 text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`}><Trash2 size={12} /></Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil size={12} className="mr-1" />Edit</Button>
                          <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => { setResetPwUser(u); setResetPw(""); }} title="Reset password" data-testid={`reset-password-user-${u.id}`}><KeyRound size={12} /></Button>
                          <Button size="sm" variant="outline" className="rounded-sm h-7 text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`}><Trash2 size={12} /></Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {visibleUsers.length === 0 && (
                <div className="p-5 text-sm text-slate-500" data-testid="roster-empty-state">
                  {rosterTab === "clients" ? "No client accounts yet. Use \"Onboard client\" to invite one." : "No team members yet."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-xl">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit member" : "Onboard team member"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-overline">Name</Label><Input className="rounded-sm mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div><Label className="label-overline">Email</Label><Input className="rounded-sm mt-1" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            </div>
            {!editing && (
              <div className="rounded-sm bg-orange-50/40 border border-orange-200 p-3 text-xs text-slate-700">
                <div className="font-medium text-slate-900 mb-0.5">Invitation email</div>
                We&apos;ll email a one-time magic link to <b>{form.email || "this address"}</b>. The link is good for 7 days and lets them set their own password.
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="label-overline">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={editing?.role === "client"}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{EDIT_ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`user-role-option-${r}`}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
                {editing?.role === "client" && <p className="text-[10px] text-slate-400 mt-1">Clients can&apos;t be reassigned here.</p>}
              </div>
              <div>
                <Label className="label-overline">BU</Label>
                <Select value={form.business_unit} onValueChange={(v) => setForm({ ...form, business_unit: v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{BUS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-overline">Title</Label><Input className="rounded-sm mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            </div>
            <div>
              <Label className="label-overline">Assigned projects</Label>
              <div className="mt-2 space-y-2">
                {BUS.map((bu) => (
                  <div key={bu}>
                    <div className="text-xs font-medium text-slate-500 mb-1">{bu}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(projectsByBU[bu] || []).map((p) => {
                        const on = form.project_ids.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => toggleProject(p.id)}
                            data-testid={`user-proj-${p.id}`}
                            className={`text-xs px-2 py-1 rounded-sm border ${on ? "bg-[#F54800] text-white border-[#F54800]" : "bg-white text-slate-600 border-slate-200 hover:border-[#F54800]"}`}
                          >
                            {p.name}
                          </button>
                        );
                      })}
                      {(!projectsByBU[bu] || projectsByBU[bu].length === 0) && <span className="text-xs text-slate-400">No projects in this BU</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="save-user">{editing ? "Save" : "Send invitation"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <InvitationResultDialog data={inviteResult} onClose={() => setInviteResult(null)} />

      {/* Phase 90 — Admin/Founder instant password reset, no email required */}
      <Dialog open={!!resetPwUser} onOpenChange={(o) => !o && setResetPwUser(null)}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Reset password · {resetPwUser?.name}</DialogTitle></DialogHeader>
          <div className="mt-2">
            <Label className="label-overline">New password</Label>
            <Input
              type="password"
              className="rounded-sm mt-1"
              value={resetPw}
              onChange={(e) => setResetPw(e.target.value)}
              placeholder="At least 8 characters"
              autoFocus
              data-testid="admin-reset-password-input"
            />
            <p className="text-[11px] text-slate-500 mt-2">Takes effect immediately — no email is sent. Share the new password with {resetPwUser?.name?.split(" ")[0]} directly.</p>
          </div>
          <DialogFooter>
            <Button onClick={submitResetPassword} disabled={resetPwSaving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="admin-reset-password-submit">
              {resetPwSaving ? "Saving…" : "Reset password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isFounder && (
        <MergeUsersDialog
          users={users}
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          onDone={load}
        />
      )}

      <DangerZone />
    </div>
  );
}

function InvitationResultDialog({ data, onClose }) {
  const copy = async () => {
    if (!data?.link) return;
    try { await navigator.clipboard.writeText(data.link); toast.success("Link copied"); }
    catch { toast.error("Could not copy — select & copy manually"); }
  };
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="invite-result-dialog">
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Mail size={16} className="text-[#F54800]" /> Invitation sent</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-700 mt-2">
              An email is on its way to <b>{data.email}</b> with a one-time link to set a password.
              The link is valid for 7 days.
            </p>
            <div className="mt-3 rounded-sm bg-slate-50 border border-slate-200 p-3">
              <div className="label-overline mb-1.5 text-slate-500">Direct link (copy & share if the email doesn&apos;t arrive)</div>
              <div className="font-mono text-[11px] text-slate-700 break-all" data-testid="invite-link">{data.link}</div>
              <Button size="sm" variant="outline" className="rounded-sm mt-2 h-7" onClick={copy} data-testid="copy-invite-link">
                <Copy size={12} className="mr-1" /> Copy link
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={onClose} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const reset = async () => {
    if (confirm !== "WIPE") { toast.error('Type "WIPE" exactly to confirm.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-demo-data", { confirm: "WIPE" });
      const total = Object.values(data.counts || {}).reduce((s, n) => s + (n || 0), 0);
      toast.success(`Wiped ${total} demo records. Kept ${data.kept_admin}.`);
      setOpen(false);
      setConfirm("");
      // Hard reload so every page refetches its now-empty data
      setTimeout(() => { window.location.href = "/dashboard"; }, 800);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const targetedCleanup = async () => {
    if (!window.confirm("Run targeted cleanup? This removes TEST_ records, anything tagged 'Dummy Data', and duplicate projects. Your real data stays.")) return;
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-test-data", { confirm: "WIPE" });
      const total = Object.values(data.deleted || {}).reduce((s, n) => s + (n || 0), 0);
      toast.success(`Cleaned ${total} test/dummy/duplicate record${total === 1 ? "" : "s"}.`);
      setTimeout(() => window.location.reload(), 700);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCleanupBusy(false); }
  };

  const [seedPurgeBusy, setSeedPurgeBusy] = useState(false);
  const runSeedPurge = async () => {
    setSeedPurgeBusy(true);
    try {
      const { data: preview } = await api.get("/admin/seed-users/preview");
      if (!preview.count) { toast.success("No leftover TEST_iter/Fresh/Pending seed users found."); return; }
      const names = preview.users.map((u) => `${u.name} (${u.email})`);
      const shown = names.slice(0, 15).join("\n") + (names.length > 15 ? `\n…and ${names.length - 15} more` : "");
      if (!window.confirm(`Delete ${preview.count} leftover seed/test user(s)?\n\n${shown}`)) return;
      const { data } = await api.delete("/admin/seed-users");
      toast.success(`Purged ${data.deleted} seed user${data.deleted === 1 ? "" : "s"}.`);
      setTimeout(() => window.location.reload(), 600);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSeedPurgeBusy(false); }
  };

  const [vendorCleanupBusy, setVendorCleanupBusy] = useState(false);
  const runVendorLeaveCleanup = async () => {
    if (!window.confirm("Remove any leave records/balance adjustments tied to Vendor accounts? Vendors should never have these — real employee data is untouched.")) return;
    setVendorCleanupBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-vendor-leaves");
      toast.success(`Removed ${data.leaves} leave record(s) and ${data.adjustments} adjustment(s) tied to vendor accounts.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setVendorCleanupBusy(false); }
  };

  const [restrictBusy, setRestrictBusy] = useState(false);
  const [restrictEmails, setRestrictEmails] = useState("");
  const runRestrictAdmin = async () => {
    const emails = restrictEmails.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error("Enter at least one canonical founder email."); return; }
    if (!window.confirm(
      `Elevate these accounts to founder:\n\n${emails.join("\n")}\n\n` +
      `Every OTHER admin/founder will be demoted to client_manager (per-page module access can be re-granted individually). Idempotent.`
    )) return;
    setRestrictBusy(true);
    try {
      const { data } = await api.post("/admin/restrict-admin-access", {
        founder_emails: emails,
        demote_role: "client_manager",
        confirm: "RESTRICT",
      });
      const r = data.report || {};
      toast.success(`Elevated ${r.elevated?.length || 0} · demoted ${r.demoted?.length || 0} · missing ${r.not_found?.length || 0}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestrictBusy(false); }
  };

  return (
    <div className="mt-12 border border-amber-200 bg-amber-50/40 rounded-sm p-5" data-testid="danger-zone">
      <div className="label-overline text-amber-700 mb-2">Maintenance</div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="min-w-0 max-w-2xl">
          <div className="font-display font-bold text-slate-900">Targeted cleanup</div>
          <p className="text-sm text-slate-600 mt-1">
            Removes only TEST_-prefixed records, anything containing &quot;Dummy Data&quot;, and duplicate
            projects (keeps the oldest). Safe to run any time — your real users and live projects are untouched.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={targetedCleanup}
          disabled={cleanupBusy}
          className="rounded-sm border-amber-400 text-amber-800 hover:bg-amber-100"
          data-testid="cleanup-test-btn"
        >
          <Trash2 size={14} className="mr-1.5" /> {cleanupBusy ? "Cleaning…" : "Clean test/dummy data"}
        </Button>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-5 border-t border-amber-200 pt-5">
        <div className="min-w-0 max-w-2xl">
          <div className="font-display font-bold text-slate-900">Purge test/seed users</div>
          <p className="text-sm text-slate-600 mt-1">
            Removes leftover placeholder team members from past testing rounds — names starting with
            &quot;TEST_iter&quot;, or exactly &quot;Fresh User&quot;/&quot;Pending User&quot;. Shows a
            preview list before anything is deleted.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={runSeedPurge}
          disabled={seedPurgeBusy}
          className="rounded-sm border-amber-400 text-amber-800 hover:bg-amber-100"
          data-testid="purge-seed-users-btn"
        >
          <Trash2 size={14} className="mr-1.5" /> {seedPurgeBusy ? "Checking…" : "Purge test/seed users"}
        </Button>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-5 border-t border-amber-200 pt-5">
        <div className="min-w-0 max-w-2xl">
          <div className="font-display font-bold text-slate-900">Vendor leave data cleanup</div>
          <p className="text-sm text-slate-600 mt-1">
            Removes any leave records or balance adjustments mistakenly tied to Vendor accounts
            (vendors are excluded from Leaves/Insights and should never have these). Run once after
            deploying — vendors are now blocked from creating new ones going forward.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={runVendorLeaveCleanup}
          disabled={vendorCleanupBusy}
          className="rounded-sm border-amber-400 text-amber-800 hover:bg-amber-100"
          data-testid="cleanup-vendor-leaves-btn"
        >
          <Trash2 size={14} className="mr-1.5" /> {vendorCleanupBusy ? "Cleaning…" : "Clean up vendor leave data"}
        </Button>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-5 border-t border-amber-200 pt-5">
        <div className="min-w-0 max-w-2xl">
          <div className="font-display font-bold text-slate-900">Restrict admin access</div>
          <p className="text-sm text-slate-600 mt-1">
            Keeps only the canonical founder emails at role <code className="bg-slate-100 px-1 rounded">founder</code>.
            Every other admin/founder is demoted to <code className="bg-slate-100 px-1 rounded">client_manager</code>
            with per-page module access defaults (edit individually via <strong>Access Management</strong>).
          </p>
          <Input
            className="mt-2 max-w-xl"
            placeholder="vt@analogydesign.co, pundalik@analogydesign.co"
            value={restrictEmails}
            onChange={(e) => setRestrictEmails(e.target.value)}
            data-testid="restrict-admin-emails"
          />
        </div>
        <Button
          variant="outline"
          onClick={runRestrictAdmin}
          disabled={restrictBusy || !restrictEmails.trim()}
          className="rounded-sm border-amber-400 text-amber-800 hover:bg-amber-100"
          data-testid="restrict-admin-btn"
        >
          <ShieldCheck size={14} className="mr-1.5" /> {restrictBusy ? "Restricting…" : "Restrict admin access"}
        </Button>
      </div>

      <div className="border-t border-red-200 pt-5">
        <div className="label-overline text-red-700 mb-2">Danger zone</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 max-w-2xl">
            <div className="font-display font-bold text-slate-900">Clear ALL demo data (nuclear)</div>
            <p className="text-sm text-slate-600 mt-1">
              Wipes seeded sample employees, projects, milestones, chat history, assets, leaves, contracts,
              deliverables, weekly reports — everything except your admin account. Sets a flag that prevents
              demo from being re-seeded on future backend restarts.
            </p>
            <p className="text-xs text-slate-500 mt-2 italic">
              This cannot be undone. Use only when going fully live.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setOpen(true)}
            className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
            data-testid="open-reset-demo-btn"
          >
            <Trash2 size={14} className="mr-1.5" /> Wipe everything
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) setConfirm(""); } }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="reset-demo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-red-700">Wipe demo data?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <p className="text-sm text-slate-700">
              You&apos;re about to permanently delete <b>all sample users, projects, milestones, chats,
              leaves, assets, contracts, deliverables, weekly summaries, and audit log entries</b>.
            </p>
            <p className="text-sm text-slate-700">
              Your admin account stays. Real users you&apos;ve already onboarded will also be removed,
              so only do this when you&apos;re ready to start the live environment from a blank slate.
            </p>
            <div>
              <Label className="label-overline">Type <span className="font-mono text-red-700">WIPE</span> to confirm</Label>
              <Input
                className="rounded-sm mt-1 font-mono"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="WIPE"
                autoFocus
                data-testid="reset-demo-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setConfirm(""); }} disabled={busy} className="rounded-sm" data-testid="reset-demo-cancel">Cancel</Button>
            <Button
              onClick={reset}
              disabled={confirm !== "WIPE" || busy}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              data-testid="reset-demo-confirm"
            >
              {busy ? "Wiping…" : "Yes, wipe everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
